import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { SortingState } from '@tanstack/react-table';
import { MdAdd } from 'react-icons/md';
import { useNavigate, Outlet } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';

import DataTable from 'components/Common/DataTable';
import Search from 'components/Common/Search';
import Button from 'components/Common/Button';
import Select from '@/components/Common/Select';
import Pagination, { usePagination } from '@/components/Common/Pagination';
import { useGetCoupons, useDeleteCoupon } from 'apis/agency/coupons/hook';
import { getCouponColumns } from './utils/columns';

export default function Coupons() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchText, setSearchText] = useState<null | string>(null);
  const { currentPage, perPage, setPage, setPerPage } = usePagination();

  const { control } = useForm({
    defaultValues: {
      coupon_type_eq: ' ',
      status_eq: ' '
    }
  });

  const filters = useWatch({ control });

  const { data: couponsData, isLoading } = useGetCoupons({
    page: currentPage,
    per_page: perPage,

    filter: { ...filters, code_cont: searchText }
  });

  const deleteCouponMutation = useDeleteCoupon();

  const statusOptions = [
    { value: ' ', label: 'All Status' },
    { value: 'active', label: 'Active' },
    { value: 'archived', label: 'Archived' }
  ];

  const typesOptions = [
    { value: ' ', label: 'All Types' },
    { value: 'generic', label: 'Generic' },
    { value: 'business_specific', label: 'Business Specific' }
  ];

  const columns = useMemo(
    () =>
      getCouponColumns({
        onDelete: (id) => deleteCouponMutation.mutate(id),
        onEdit: (id) => navigate(`/agency/coupons/${id}`)
      }),
    [deleteCouponMutation, navigate]
  );

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      <Box
        display='flex'
        flexDirection={{ xs: 'column', lg: 'row' }}
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        justifyContent='space-between'
        gap={{ xs: 2, lg: 0 }}
        mb={3}
      >
        <Box>
          <Typography variant='h6' fontWeight='500' sx={{ fontSize: { xs: '1.125rem', lg: 20 } }}>
            Coupon Management
          </Typography>
          <Typography sx={{ fontSize: { xs: '14px', lg: 16 } }} mt={0.5} color='secondary'>
            Create and manage promotional coupons for your business.
          </Typography>
        </Box>
        <Button
          startIcon={<MdAdd />}
          variant='contained'
          onClick={() => navigate('/agency/coupons/add')}
          sx={{ width: { xs: '100%', lg: 'auto' }, minWidth: { lg: 'auto' } }}
        >
          Create Coupon
        </Button>
      </Box>
      <Box
        display='flex'
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={2}
        justifyContent='space-between'
        mb={3}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <Box minWidth={{ xs: '100%', lg: '32%' }} width={{ xs: '100%', lg: 'auto' }}>
          <Search
            size='small'
            placeholder='Search coupons by code...'
            setSearchText={setSearchText}
            searchText={searchText}
            fullWidth
          />
        </Box>
        <Box minWidth={{ xs: '100%', lg: '32%' }} width={{ xs: '100%', lg: 'auto' }} alignItems={'baseline'}>
          <Select
            name='coupon_type_eq'
            label='Type'
            control={control}
            options={typesOptions}
            sortOrder='none'
            fullWidth
            size='small'
            sx={{ background: '#fff', maxHeight: '40px' }}
          />
        </Box>
        <Box minWidth={{ xs: '100%', lg: '32%' }} width={{ xs: '100%', lg: 'auto' }}>
          <Select
            name='status_eq'
            label='Status'
            control={control}
            sortOrder='none'
            fullWidth
            options={statusOptions}
            size='small'
            sx={{ background: '#fff', maxHeight: '40px' }}
          />
        </Box>
      </Box>
      <DataTable
        data={couponsData?.data || []}
        columns={columns}
        isLoading={isLoading}
        sorting={sorting}
        onSortingChange={setSorting}
        containerStyles={{ minHeight: 400 }}
      />
      <Pagination
        totalPages={couponsData?.meta?.total_pages}
        totalCount={couponsData?.meta?.total_count ?? 0}
        currentPage={currentPage}
        perPage={perPage}
        onChange={setPage}
        onPerPageChange={setPerPage}
      />
      <Outlet />
    </Box>
  );
}
